import { useState } from 'react';
import QRCode from 'react-qr-code';
import { Check, Copy, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Mobile App — Verbindung per QR.
 *
 * Jede Vyndesk-Instanz hat ihre eigene URL; die Mobile App muss wissen,
 * mit welchem Server sie spricht. Der QR-Code trägt genau diese URL als
 * Deep-Link (vyndesk://connect?url=…). Scannen verbindet die App mit
 * dieser Instanz — angemeldet wird danach ganz normal in der App.
 */
const MobileAppSection = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // QR, bu frontend'in GERÇEKTEN konuştuğu API tabanını taşır. Bazı
  // instance'larda API ayrı bir adrestedir (VITE_API_URL); same-origin
  // kurulumlarda '/api' path'i origin'e eklenir. Desk origin'i yazmak
  // yetmez — mobil uygulama aksi halde yanlış adrese bağlanır.
  const rawApiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  const apiUrl = rawApiBase.startsWith('http')
    ? rawApiBase.replace(/\/$/, '')
    : window.location.origin + rawApiBase.replace(/\/$/, '');
  const deepLink = `vyndesk://connect?url=${encodeURIComponent(apiUrl)}`;

  const copyUrl = () => {
    void navigator.clipboard.writeText(apiUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-transparent">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#0F99FD] dark:bg-white/[0.06]">
            <Smartphone className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t('settings:mobile.connectTitle', 'Mit der Vyndesk Mobile App verbinden')}
            </h3>
            <p className="text-xs text-slate-500">
              {t('settings:mobile.connectHint', 'QR-Code mit der App scannen — sie verbindet sich mit diesem Desk. Angemeldet wird danach in der App.')}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10">
            <QRCode value={deepLink} size={172} fgColor="#101319" bgColor="#ffffff" />
          </div>

          <div className="min-w-0 space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                {t('settings:mobile.apiUrl', 'API-URL dieser Instanz')}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 dark:bg-white/[0.06] dark:text-slate-200" dir="ltr">
                  {apiUrl}
                </code>
                <button
                  type="button"
                  onClick={copyUrl}
                  className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/[0.06]"
                  title={t('settings:mobile.copyUrl', 'URL kopieren')}
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <p className="max-w-md text-xs leading-5 text-slate-500">
              {t('settings:mobile.fallbackHint', 'Ohne Kamera: Diese API-URL kann in der App auch manuell eingegeben werden. Der QR-Code enthält keine Zugangsdaten — nur die Adresse dieser Instanz.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppSection;
